import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Divider,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid as MuiGrid,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { addressApi } from '../../api/auth';
import { Address, AddressCreate, AddressUpdate } from '../../types/user';

// Define Grid component with proper React memo to prevent hooks issues
const Grid = React.memo(function Grid(props: any) {
  return <MuiGrid {...props} />;
});

const AddressManagement: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<number | null>(null);
  
  const [formData, setFormData] = useState<AddressCreate>({
    name: '',
    street: '',
    city: '',
    country_code: 'CA',
    postal_code: '',
    state_or_province_code: 'BC',
    phone: '',
    email: '',
    is_default: false,
    delivery_notes: ''
  });
  
  useEffect(() => {
    fetchAddresses();
  }, []);
  
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await addressApi.getAddresses();
      setAddresses(data);
    } catch (err: any) {
      console.error('Error fetching addresses:', err);
      // Handle validation errors
      if (err.response?.data?.detail) {
        // If the error is an object with validation details, convert to string
        if (typeof err.response.data.detail === 'object') {
          setError(JSON.stringify(err.response.data.detail));
        } else {
          setError(String(err.response.data.detail));
        }
      } else {
        setError('Failed to fetch addresses');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_default' ? checked : value
    }));
  };
  
  const handleAddAddress = () => {
    setFormData({
      name: '',
      street: '',
      city: '',
      country_code: 'CA',
      postal_code: '',
      state_or_province_code: 'BC',
      phone: '',
      email: '',
      is_default: false,
      delivery_notes: ''
    });
    setEditingAddressId(null);
    setDialogOpen(true);
  };
  
  const handleEditAddress = (address: Address) => {
    setFormData({
      name: address.name,
      street: address.street,
      city: address.city,
      country_code: address.country_code,
      postal_code: address.postal_code,
      state_or_province_code: address.state_or_province_code || '',
      phone: address.phone || '',
      email: address.email || '',
      is_default: address.is_default,
      delivery_notes: address.delivery_notes || ''
    });
    setEditingAddressId(address.id);
    setDialogOpen(true);
  };
  
  const handleDeleteClick = (id: number) => {
    setAddressToDelete(id);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteAddress = async () => {
    if (!addressToDelete) return;
    
    try {
      setLoading(true);
      setError(null);
      await addressApi.deleteAddress(addressToDelete);
      setAddresses(prev => prev.filter(addr => addr.id !== addressToDelete));
      setSuccess('Address deleted successfully');
      setDeleteConfirmOpen(false);
      setAddressToDelete(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting address:', err);
      // Handle validation errors
      if (err.response?.data?.detail) {
        // If the error is an object with validation details, convert to string
        if (typeof err.response.data.detail === 'object') {
          setError(JSON.stringify(err.response.data.detail));
        } else {
          setError(String(err.response.data.detail));
        }
      } else {
        setError('Failed to delete address');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      if (editingAddressId) {
        // Update existing address
        const updateData: AddressUpdate = { ...formData };
        await addressApi.updateAddress(editingAddressId, updateData);
        setSuccess('Address updated successfully');
      } else {
        // Create new address
        await addressApi.createAddress(formData);
        setSuccess('Address created successfully');
      }
      
      // Refresh address list
      await fetchAddresses();
      setDialogOpen(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Address error:', err);
      // Handle validation errors
      if (err.response?.data?.detail) {
        // If the error is an object with validation details, convert to string
        if (typeof err.response.data.detail === 'object') {
          setError(JSON.stringify(err.response.data.detail));
        } else {
          setError(String(err.response.data.detail));
        }
      } else {
        setError('Failed to save address');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            My Addresses
          </Typography>
          
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddAddress}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2 }}
          >
            Add Address
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {loading && !addresses.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : addresses.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 6, 
            backgroundColor: 'rgba(0,0,0,0.02)', 
            borderRadius: 2 
          }}>
            <HomeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography align="center" color="text.secondary">
              You don't have any saved addresses yet.
            </Typography>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={handleAddAddress} 
              sx={{ mt: 2, borderRadius: 2 }}
            >
              Add Your First Address
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {addresses.map((address) => (
              <Grid item xs={12} md={6} key={address.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    position: 'relative',
                    p: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  {address.is_default && (
                    <Chip 
                      label="Default" 
                      color="primary" 
                      size="small" 
                      icon={<HomeIcon />}
                      sx={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12,
                        borderRadius: '12px'
                      }} 
                    />
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {address.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      {address.street}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {address.city}, {address.state_or_province_code} {address.postal_code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {address.country_code === 'CA' ? 'Canada' : 'United States'}
                    </Typography>
                    
                    {address.phone && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Phone: {address.phone}
                      </Typography>
                    )}
                    
                    {address.delivery_notes && (
                      <Box sx={{ mt: 2, px: 2, py: 1, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Delivery Notes:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {address.delivery_notes}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditAddress(address)}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      Edit
                    </Button>
                    
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(address.id)}
                      color="error"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        {/* Add/Edit Address Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
            {editingAddressId ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EditIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Edit Address</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AddIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Add New Address</Typography>
              </Box>
            )}
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="phone"
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Address Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="street"
                    label="Street Address"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="city"
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="state_or_province_code"
                    label="Province"
                    name="state_or_province_code"
                    value={formData.state_or_province_code}
                    onChange={handleChange}
                    select
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  >
                    <MenuItem value="BC">British Columbia</MenuItem>
                    <MenuItem value="AB">Alberta</MenuItem>
                    <MenuItem value="ON">Ontario</MenuItem>
                    <MenuItem value="QC">Quebec</MenuItem>
                    <MenuItem value="MB">Manitoba</MenuItem>
                    <MenuItem value="SK">Saskatchewan</MenuItem>
                    <MenuItem value="NS">Nova Scotia</MenuItem>
                    <MenuItem value="NB">New Brunswick</MenuItem>
                    <MenuItem value="NL">Newfoundland and Labrador</MenuItem>
                    <MenuItem value="PE">Prince Edward Island</MenuItem>
                    <MenuItem value="YT">Yukon</MenuItem>
                    <MenuItem value="NT">Northwest Territories</MenuItem>
                    <MenuItem value="NU">Nunavut</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="postal_code"
                    label="Postal Code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    id="country_code"
                    label="Country"
                    name="country_code"
                    value={formData.country_code}
                    onChange={handleChange}
                    select
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  >
                    <MenuItem value="CA">Canada</MenuItem>
                    <MenuItem value="US">United States</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="delivery_notes"
                    label="Delivery Notes"
                    name="delivery_notes"
                    multiline
                    rows={2}
                    value={formData.delivery_notes}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Special delivery instructions, gate codes, preferred time, etc."
                    InputProps={{
                      sx: { borderRadius: 1 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_default}
                        onChange={handleChange}
                        name="is_default"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Set as default address
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={handleCloseDialog} 
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
              sx={{ borderRadius: 2, ml: 1 }}
            >
              {loading ? 'Saving...' : editingAddressId ? 'Update Address' : 'Save Address'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this address? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteAddress} color="error" variant="contained">
              {loading ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AddressManagement; 